import { formatCurrency } from "@/utils/formatCurrency";
import { interpretarFinDePublicacion } from "@/hooks/react-query/BigSuppliers/resultadoDePublicar";
import { resultadoDelAumentoAplicado } from "@/hooks/react-query/BigSuppliers/resultadoDelAumentoAplicado";

// Notification types - New format
// Convention: {domain}.{entity}.{action}
export const NOTIFICATION_TYPES = {
  // Board domain
  BOARD_CREATED: "board.created",
  BOARD_TASK_CREATED: "board.task.created",
  BOARD_TASK_UPDATED: "board.task.updated",
  BOARD_TASK_MOVED: "board.task.moved",
  BOARD_TASK_COMPLETED: "board.task.completed",
  BOARD_TASK_ASSIGNED: "board.task.assigned",
  BOARD_TASK_UNASSIGNED: "board.task.unassigned",

  // Big Supplier domain
  BIG_SUPPLIER_CONNECTION_REQUESTED: "big_supplier.connection.requested",
  BIG_SUPPLIER_CONNECTION_APPROVED: "big_supplier.connection.approved",
  BIG_SUPPLIER_CONNECTION_REJECTED: "big_supplier.connection.rejected",
  BIG_SUPPLIER_CONNECTION_EXPIRED: "big_supplier.connection.expired",
  BIG_SUPPLIER_PRODUCT_IMPORT_COMPLETED:
    "big_supplier.product_import.completed",
  BIG_SUPPLIER_PRODUCT_PUBLISH_COMPLETED:
    "big_supplier.product_publish.completed",
  BIG_SUPPLIER_PRICE_UPDATE_SCHEDULED: "big_supplier.price_update.scheduled",
  BIG_SUPPLIER_PRICE_UPDATE_PUBLISHED: "big_supplier.price_update.published",
  BIG_SUPPLIER_PRICE_UPDATE_SUPPLIER_APPLIED:
    "big_supplier.price_update.supplier_applied",
  BIG_SUPPLIER_B2B_ORDER_STATUS_CHANGED:
    "big_supplier.b2b_order.status_changed",
  BIG_SUPPLIER_B2B_ORDER_RECEIVED: "big_supplier.b2b_order.received",
  BIG_SUPPLIER_B2B_PAYMENT_REQUESTED: "big_supplier.b2b_payment.requested",
  BIG_SUPPLIER_B2B_PAYMENT_STATUS_CHANGED:
    "big_supplier.b2b_payment.status_changed",

  // Inventory domain
  INVENTORY_BULK_PROCESSED: "inventory.bulk.processed",
  INVENTORY_BULK_COMPLETED: "inventory.bulk.completed",
  INVENTORY_CSV_PROCESSED: "inventory.csv.processed",
  INVENTORY_CSV_FAILED: "inventory.csv.failed",
  INVENTORY_WAREHOUSE_INITIALIZED: "inventory.warehouse.initialized",
  INVENTORY_TRANSFER_CREATED: "inventory.transfer.created",
  INVENTORY_TRANSFER_APPROVED: "inventory.transfer.approved",
  INVENTORY_TRANSFER_IN_TRANSIT: "inventory.transfer.in_transit",
  INVENTORY_TRANSFER_COMPLETED: "inventory.transfer.completed",
  INVENTORY_TRANSFER_CANCELLED: "inventory.transfer.cancelled",
  INVENTORY_TRANSFER_REJECTED: "inventory.transfer.rejected",

  // Pricing domain
  PRICING_BULK_COMPLETED: "pricing.bulk.completed",
  PRICING_CSV_PROCESSED: "pricing.csv.processed",
} as const;

interface NotificationInfo {
  title: string;
  description: string;
}

const notificationTranslations: Record<string, NotificationInfo> = {
  // Board domain
  [NOTIFICATION_TYPES.BOARD_CREATED]: {
    title: "Tablero creado",
    description: "Se ha creado un nuevo tablero.",
  },
  [NOTIFICATION_TYPES.BOARD_TASK_CREATED]: {
    title: "Tarea creada",
    description: "Se ha creado una nueva tarea.",
  },
  [NOTIFICATION_TYPES.BOARD_TASK_UPDATED]: {
    title: "Tarea actualizada",
    description: "Una tarea ha sido actualizada.",
  },
  [NOTIFICATION_TYPES.BOARD_TASK_MOVED]: {
    title: "Tarea movida",
    description: "Una tarea ha sido movida a otra columna.",
  },
  [NOTIFICATION_TYPES.BOARD_TASK_COMPLETED]: {
    title: "Tarea completada",
    description: "Una tarea ha sido marcada como completada.",
  },
  [NOTIFICATION_TYPES.BOARD_TASK_ASSIGNED]: {
    title: "Tarea asignada",
    description: "Te han asignado una nueva tarea.",
  },
  [NOTIFICATION_TYPES.BOARD_TASK_UNASSIGNED]: {
    title: "Tarea desasignada",
    description: "Te han removido de una tarea.",
  },

  // Big Supplier domain
  [NOTIFICATION_TYPES.BIG_SUPPLIER_B2B_PAYMENT_REQUESTED]: {
    title: "Pago recibido de un cliente",
    description: "Un cliente registró un pago y espera tu autorización.",
  },
  [NOTIFICATION_TYPES.BIG_SUPPLIER_B2B_PAYMENT_STATUS_CHANGED]: {
    title: "Cambió el estado de un pago B2B",
    description: "Se actualizó el estado de un pago con un negocio conectado.",
  },
  [NOTIFICATION_TYPES.BIG_SUPPLIER_CONNECTION_REQUESTED]: {
    title: "Solicitud de conexión recibida",
    description: "Un negocio quiere conectarse contigo.",
  },
  [NOTIFICATION_TYPES.BIG_SUPPLIER_CONNECTION_APPROVED]: {
    title: "Conexión aprobada",
    description: "Tu solicitud de conexión ha sido aprobada.",
  },
  [NOTIFICATION_TYPES.BIG_SUPPLIER_CONNECTION_REJECTED]: {
    title: "Conexión rechazada",
    description: "Tu solicitud de conexión ha sido rechazada.",
  },
  [NOTIFICATION_TYPES.BIG_SUPPLIER_CONNECTION_EXPIRED]: {
    title: "Conexión expirada",
    description: "Tu solicitud de conexión ha expirado.",
  },
  [NOTIFICATION_TYPES.BIG_SUPPLIER_PRODUCT_IMPORT_COMPLETED]: {
    title: "Importación de productos",
    description: "La importación de productos del proveedor ha finalizado.",
  },
  [NOTIFICATION_TYPES.BIG_SUPPLIER_PRODUCT_PUBLISH_COMPLETED]: {
    title: "Publicación de productos completada",
    description:
      "La publicación de productos en el catálogo B2B ha finalizado.",
  },
  [NOTIFICATION_TYPES.BIG_SUPPLIER_PRICE_UPDATE_SCHEDULED]: {
    title: "Actualización de precios programada",
    description:
      "Un proveedor ha programado una actualización de precios próximamente.",
  },
  [NOTIFICATION_TYPES.BIG_SUPPLIER_PRICE_UPDATE_PUBLISHED]: {
    title: "Actualización de precios",
    description: "Un proveedor ha publicado una actualización de precios.",
  },
  [NOTIFICATION_TYPES.BIG_SUPPLIER_PRICE_UPDATE_SUPPLIER_APPLIED]: {
    title: "Precios publicados actualizados",
    description: "Terminó la actualización de tus precios publicados.",
  },
  [NOTIFICATION_TYPES.BIG_SUPPLIER_B2B_ORDER_STATUS_CHANGED]: {
    title: "Pedido B2B actualizado",
    description: "Un proveedor actualizó el estado de tu pedido.",
  },
  [NOTIFICATION_TYPES.BIG_SUPPLIER_B2B_ORDER_RECEIVED]: {
    title: "Nuevo pedido B2B",
    description: "Recibiste un nuevo pedido de un cliente B2B.",
  },

  // Inventory domain
  [NOTIFICATION_TYPES.INVENTORY_BULK_PROCESSED]: {
    title: "Inventario procesado",
    description: "El inventario ha sido procesado correctamente.",
  },
  [NOTIFICATION_TYPES.INVENTORY_BULK_COMPLETED]: {
    title: "Actualización de inventario finalizada",
    description: "La actualización masiva de inventario ha finalizado.",
  },
  [NOTIFICATION_TYPES.INVENTORY_CSV_PROCESSED]: {
    title: "CSV de inventario procesado",
    description:
      "El archivo CSV de inventario ha sido procesado correctamente.",
  },
  [NOTIFICATION_TYPES.INVENTORY_CSV_FAILED]: {
    title: "Error en CSV de inventario",
    description: "Ocurrió un error al procesar el archivo CSV de inventario.",
  },
  [NOTIFICATION_TYPES.INVENTORY_WAREHOUSE_INITIALIZED]: {
    title: "Depósito inicializado",
    description: "El depósito ha sido inicializado correctamente.",
  },
  [NOTIFICATION_TYPES.INVENTORY_TRANSFER_CREATED]: {
    title: "Nueva solicitud de transferencia",
    description:
      "Se ha creado una nueva solicitud de transferencia de inventario.",
  },
  [NOTIFICATION_TYPES.INVENTORY_TRANSFER_APPROVED]: {
    title: "Transferencia aprobada",
    description: "La transferencia de inventario ha sido aprobada.",
  },
  [NOTIFICATION_TYPES.INVENTORY_TRANSFER_IN_TRANSIT]: {
    title: "Transferencia en tránsito",
    description: "La transferencia de inventario está en camino.",
  },
  [NOTIFICATION_TYPES.INVENTORY_TRANSFER_COMPLETED]: {
    title: "Transferencia completada",
    description: "La transferencia de inventario ha sido completada.",
  },
  [NOTIFICATION_TYPES.INVENTORY_TRANSFER_CANCELLED]: {
    title: "Transferencia cancelada",
    description: "La transferencia de inventario ha sido cancelada.",
  },
  [NOTIFICATION_TYPES.INVENTORY_TRANSFER_REJECTED]: {
    title: "Transferencia rechazada",
    description: "La transferencia de inventario ha sido rechazada.",
  },

  // Pricing domain
  [NOTIFICATION_TYPES.PRICING_BULK_COMPLETED]: {
    title: "Actualización de precios completada",
    description: "La actualización masiva de precios ha finalizado.",
  },
  [NOTIFICATION_TYPES.PRICING_CSV_PROCESSED]: {
    title: "CSV de precios procesado",
    description: "El archivo CSV de precios ha sido procesado correctamente.",
  },
};

/**
 * Returns the notification info (title and description) for a given type.
 * Returns null if the type is not found in translations.
 */
export const notificationTitle = (
  notificationType: string
): NotificationInfo | null => {
  return notificationTranslations[notificationType] || null;
};

type Payload = Record<string, any>;

/**
 * Los eventos que core manda con el tipo `big_supplier.b2b_order.received`,
 * distinguidos por `payload.event` (`notifySupplier` en
 * `b2b-order-activities.service.ts`). Sin esto, una cancelación o un cambio
 * de cantidades se leían "Recibiste un nuevo pedido de un cliente B2B".
 */
const EVENTOS_DEL_PEDIDO_B2B: Record<
  string,
  (payload: Payload) => Partial<NotificationInfo>
> = {
  quantities_updated: (payload) => {
    const detalles = payload?.details;
    const cambios: number = detalles?.changes?.length ?? 0;
    if (
      typeof detalles?.previousTotal !== "number" ||
      typeof detalles?.total !== "number"
    ) {
      return {
        title: "Cambiaron las cantidades de un pedido B2B",
        description:
          "El comprador cambió las cantidades de un pedido pendiente de aprobación.",
      };
    }
    return {
      title: "Cambiaron las cantidades de un pedido B2B",
      description: `El comprador cambió ${
        cambios === 1 ? "1 producto" : `${cambios} productos`
      }: el total pasó de ${formatCurrency(
        detalles.previousTotal
      )} a ${formatCurrency(detalles.total)}.`,
    };
  },
  cancelled: () => ({
    title: "Pedido B2B cancelado",
    description: "El comprador canceló el pedido.",
  }),
  delivery_confirmed: () => ({
    title: "Entrega confirmada",
    description: "El comprador confirmó que recibió el pedido.",
  }),
};

/**
 * Los tipos cuyo texto depende del payload. Devuelven sólo lo que cambia
 * respecto del texto del tipo, o `null` para quedarse con ese.
 */
const SEGUN_EL_PAYLOAD: Record<
  string,
  (payload: Payload) => Partial<NotificationInfo> | null
> = {
  [NOTIFICATION_TYPES.BIG_SUPPLIER_B2B_ORDER_RECEIVED]: (payload) =>
    EVENTOS_DEL_PEDIDO_B2B[payload?.event]?.(payload) ?? null,

  // Los mismos conteos que el aviso de cierre de "Publicar": cuántos se
  // publicaron y cuántos se saltearon. Una notificación vieja sin conteos se
  // queda con el texto del tipo.
  [NOTIFICATION_TYPES.BIG_SUPPLIER_PRODUCT_PUBLISH_COMPLETED]: (payload) =>
    typeof payload?.successCount === "number"
      ? { description: interpretarFinDePublicacion(payload).toast?.description }
      : null,
  [NOTIFICATION_TYPES.BIG_SUPPLIER_PRICE_UPDATE_SUPPLIER_APPLIED]: (payload) =>
    typeof payload?.successCount === "number"
      ? { description: resultadoDelAumentoAplicado(payload).toast?.description }
      : null,
};

/**
 * Título y descripción de una notificación: lo que leen la campanita, la
 * página de notificaciones y el aviso del sistema operativo. Las tres leían
 * sólo el tipo, y un mismo tipo puede decir cosas distintas según el payload.
 *
 * El título del server gana, como ya hacían la campanita y la página. `null`
 * si el tipo no se conoce: así lo trataba el aviso del sistema operativo.
 */
export const describirNotificacion = (
  notification:
    | { type?: string | null; title?: string | null; payload?: Payload | null }
    | null
    | undefined
): NotificationInfo | null => {
  const tipo = notification?.type ?? "";
  const base = notificationTranslations[tipo];
  if (!base) return null;
  const segunPayload = SEGUN_EL_PAYLOAD[tipo]?.(notification?.payload ?? {});
  return {
    title: notification?.title || segunPayload?.title || base.title,
    description: segunPayload?.description ?? base.description,
  };
};

export const getLink = (notification: any) => {
  const notificationType = notification.notification.type;
  const payload = notification.notification.payload;

  switch (notificationType) {
    // Board domain
    case NOTIFICATION_TYPES.BOARD_CREATED:
    case NOTIFICATION_TYPES.BOARD_TASK_CREATED:
    case NOTIFICATION_TYPES.BOARD_TASK_UPDATED:
    case NOTIFICATION_TYPES.BOARD_TASK_MOVED:
    case NOTIFICATION_TYPES.BOARD_TASK_COMPLETED:
    case NOTIFICATION_TYPES.BOARD_TASK_ASSIGNED:
    case NOTIFICATION_TYPES.BOARD_TASK_UNASSIGNED:
      return payload?.boardId ? `/tableros/${payload.boardId}` : "/tableros";

    // Big Supplier domain - connection notifications
    case NOTIFICATION_TYPES.BIG_SUPPLIER_CONNECTION_REQUESTED:
      // Al PROVEEDOR le llega una solicitud que tiene que aprobar o rechazar, y
      // eso vive en /ventas-b2b/solicitudes. Antes lo mandaba a
      // /ventas-b2b/clientes — la lista de los que YA están conectados, donde la
      // solicitud que motivó la notificación todavía no aparece: hacías clic en
      // "Solicitud de conexión recibida" y caías en una pantalla sin ella
      // (#124). Del otro lado, al COMPRADOR que la mandó le sirve el detalle del
      // proveedor, que sí está en /proveedores.
      return payload?.role === "supplier"
        ? "/ventas-b2b/solicitudes"
        : "/proveedores?tab=big-suppliers";
    case NOTIFICATION_TYPES.BIG_SUPPLIER_CONNECTION_APPROVED:
    case NOTIFICATION_TYPES.BIG_SUPPLIER_CONNECTION_REJECTED:
    case NOTIFICATION_TYPES.BIG_SUPPLIER_CONNECTION_EXPIRED:
      return "/proveedores?tab=big-suppliers";

    case NOTIFICATION_TYPES.BIG_SUPPLIER_PRODUCT_IMPORT_COMPLETED:
      return "/inventario/productos";

    case NOTIFICATION_TYPES.BIG_SUPPLIER_PRODUCT_PUBLISH_COMPLETED:
      return "/ventas-b2b/productos";

    /**
     * El payload trae `b2bOrderId`, así que el link no lleva a la LISTA: lleva
     * a la orden de la que habla la notificación, ya filtrada. Cada lado tiene
     * su propio parámetro porque son dos pantallas distintas:
     *
     * - vendedor  → `?search=<id>` en `/ventas-b2b/ordenes` (el mismo que ya usa
     *               el link de la venta en `OrderCard`).
     * - comprador → `?b2bOrderId=<id>` en la pestaña B2B de solicitudes de compra.
     *
     * Sin el id, "El Tano Mascotas aprobó tu pedido" te dejaba en una lista
     * donde tenías que encontrar cuál de todos era (#124).
     */
    // El pago nace del lado del CLIENTE y espera al supplier: la notificación
    // de "requested" es siempre para el supplier, que la resuelve en su
    // pantalla de pagos recibidos.
    case NOTIFICATION_TYPES.BIG_SUPPLIER_B2B_PAYMENT_REQUESTED:
      return "/ventas-b2b/pagos";

    // El cambio de estado le llega a los dos lados: el supplier lo mira en sus
    // pagos recibidos y el cliente, en los suyos.
    case NOTIFICATION_TYPES.BIG_SUPPLIER_B2B_PAYMENT_STATUS_CHANGED:
      return payload?.role === "supplier"
        ? "/ventas-b2b/pagos"
        : "/compras/pagos-b2b";

    case NOTIFICATION_TYPES.BIG_SUPPLIER_B2B_ORDER_STATUS_CHANGED: {
      const b2bOrderId = payload?.b2bOrderId;
      if (payload?.role === "supplier") {
        return b2bOrderId
          ? `/ventas-b2b/ordenes?search=${encodeURIComponent(b2bOrderId)}`
          : "/ventas-b2b/ordenes";
      }
      return b2bOrderId
        ? `/compras/solicitud-compra?tab=b2b-orders&b2bOrderId=${encodeURIComponent(
            b2bOrderId
          )}`
        : "/compras/solicitud-compra?tab=b2b-orders";
    }

    case NOTIFICATION_TYPES.BIG_SUPPLIER_B2B_ORDER_RECEIVED:
      // INTERNAL mode supplier receives this in trd-desktop-new
      return payload?.b2bOrderId
        ? `/ventas-b2b/ordenes?search=${encodeURIComponent(payload.b2bOrderId)}`
        : "/ventas-b2b/ordenes";

    case NOTIFICATION_TYPES.BIG_SUPPLIER_PRICE_UPDATE_SCHEDULED:
      return "/proveedores?tab=big-suppliers&subtab=pending-updates";

    case NOTIFICATION_TYPES.BIG_SUPPLIER_PRICE_UPDATE_PUBLISHED:
      // Backend sends priceUpdateId (the supplier's price update ID)
      // We use a query param to indicate we need to look up by this ID
      return payload?.priceUpdateId
        ? `/big-supplier/pending-price-update/${payload.priceUpdateId}?byPriceUpdate=true`
        : "/proveedores?tab=big-suppliers&subtab=pending-updates";

    case NOTIFICATION_TYPES.BIG_SUPPLIER_PRICE_UPDATE_SUPPLIER_APPLIED:
      return payload?.priceUpdateId
        ? `/ventas-b2b/aumentos?aumento=${encodeURIComponent(
            payload.priceUpdateId
          )}`
        : "/ventas-b2b/aumentos";

    // Inventory domain - bulk operations
    case NOTIFICATION_TYPES.INVENTORY_BULK_PROCESSED:
    case NOTIFICATION_TYPES.INVENTORY_BULK_COMPLETED: {
      const warehouseId =
        payload?.warehouseId || notification.notification.warehouseId;
      const bulkId = payload?.bulkId;
      if (warehouseId && bulkId) {
        return `/inventario/local/${warehouseId}/${bulkId}`;
      }
      return warehouseId ? `/inventario/local/${warehouseId}` : "/inventario";
    }

    case NOTIFICATION_TYPES.INVENTORY_CSV_PROCESSED:
    case NOTIFICATION_TYPES.INVENTORY_CSV_FAILED:
    case NOTIFICATION_TYPES.INVENTORY_WAREHOUSE_INITIALIZED:
      return notification.notification.warehouseId
        ? `/inventario/local/${notification.notification.warehouseId}`
        : "/inventario";

    case NOTIFICATION_TYPES.INVENTORY_TRANSFER_CREATED:
    case NOTIFICATION_TYPES.INVENTORY_TRANSFER_APPROVED:
    case NOTIFICATION_TYPES.INVENTORY_TRANSFER_IN_TRANSIT:
    case NOTIFICATION_TYPES.INVENTORY_TRANSFER_COMPLETED:
    case NOTIFICATION_TYPES.INVENTORY_TRANSFER_CANCELLED:
    case NOTIFICATION_TYPES.INVENTORY_TRANSFER_REJECTED:
      return "/inventario/transferencia-de-mercaderia";

    // Pricing domain
    case NOTIFICATION_TYPES.PRICING_BULK_COMPLETED:
    case NOTIFICATION_TYPES.PRICING_CSV_PROCESSED:
      return "/precios/aumento-masivo";

    default:
      return "/";
  }
};
